import type { tTemplateDraftsState } from 'src/pages/WhatsappApi/hooks/useTemplateDraftsState';
import type { tTemplateDraftPayload } from 'src/pages/WhatsappApi/types/TemplateDraft.types';
import { tUndeliveredMessage } from './components/TemplatesTable/UndeliveredMessage.types';
import { tTemplateTable } from './components/TemplatesTable/TemplatesTable.types.ts';
import { tSentTemplateCounter } from './components/TemplatesTable/SentTemplateCounter.types.ts';
import {
  tOneTimeCreditProduct,
  tPaymentMethodOption,
} from './components/modals/PurchaseCreditsModal/PurchaseCreditsModal.types.ts';

import {
  eBusinessVerificationBucket,
  eBusinessVerificationStatus,
  eCanSendMessage,
  eComponentType,
  eFailureGroup,
  eFormatType,
  eParameterFormat,
  eQualityRating,
  eTemplateCategory,
  eWhatsappApiWindow,
} from './WhatsappApi.enum.ts';
import { FormikProps } from 'formik';
import { tCreateTemplateForm } from './components/CreateTemplate/CreateOrEditTemplate.types.ts';
import { eSelectedInput } from './components/CreateTemplate/CreateOrEditTemplate.enum.ts';
import { ChangeEvent } from 'react';
import { eTemplatesTable } from 'src/pages/WhatsappApi/components/TemplatesTable/TemplatesTable.enum.ts';
import { tWapiButtonRequest } from 'src/components/Chat/Chat.types.ts';

export enum eMediaType {
  IMAGE = 'image',
  VIDEO = 'video',
  PDF = 'pdf',
}

// BUG-1074: the audience rule of a WAPI campaign, owned by leads-service and
// requested with `audience=wapi_campaign`. Under it a count or a list holds
// only WhatsApp Leads; Leads of any other channel are left out. The campaign
// modal sends it with every Lead count and every Lead list it shows. Every
// caller imports the value from here.
export const CAMPAIGN_AUDIENCE = 'wapi_campaign' as const;
export type tLeadsAudience = typeof CAMPAIGN_AUDIENCE;

export type tTagsWithCountResponse = {
  success: boolean;
  code: number;
  message: string;
  data: {
    tags: Array<{ tag: string; leads_count: number }>;
    pagination: null;
  };
};

export type tCreateCampaignParameter = {
  type: string;
  value?: string;
  name?: string;
  index?: number;
  format?: string;
};

// BUG-739: dynamic button parameters (currently only COPY_CODE) sent alongside
// a campaign's body/header parameters. Reuses tWapiButtonRequest (Chat.types.ts)
// — the same shape the 1:1 chat template send already builds and the backend
// already supports — instead of redeclaring an identical, driftable type.
export type tCreateCampaignRequest = {
  campaign_id?: number | null;
  template_id: string;
  language_code: string;
  workspace_uuid: string;
  user_uuid?: string;
  send_at?: string;
  parameters?: tCreateCampaignParameter[];
  media?: { type: string; url: string; filename?: string };
  buttons?: tWapiButtonRequest[];
  name?: string;
  description?: string;
  leads?: string[];
  stages?: string[];
  tags?: string[];
  // INT-766: when true, the backend drops recipients at risk of spam blocking
  // (closed 24h window + templates already sent while closed) from the
  // resolved audience before creating the campaign. If every recipient is at
  // risk the request is rejected with 422 wba-10087 and no campaign is created.
  exclude_at_risk?: boolean;
  // INT-1221: handles to drop from the resolved audience before the campaign
  // is created — the per-contact/bulk "Excluir" action in the missing-Auto-
  // variables panel. Campaign-scoped only; never touches the CRM record.
  excluded_leads?: string[];
};

// INT-1496: a campaign draft names a real template (`template_id`, Meta's id,
// which needs a connected account), a template draft (`template_draft_uuid`,
// which does not) or neither yet. Never both: whatsapp-business-service
// answers 400. `language_code` only travels with a real template.
export type tCreateDraftCampaignRequest = Omit<
  tCreateCampaignRequest,
  'template_id' | 'language_code'
> & {
  template_id?: string;
  template_draft_uuid?: string;
  language_code?: string;
};

export type tCreateCampaignResponse = {
  message: string;
  status: string;
};

// INT-1136: same audience-selection shape as tCreateCampaignRequest so the
// creation modal can reuse the payload it already builds. At least one of the
// three lists must be non-empty; the endpoint is read-only.
export type tCampaignAtRiskPreviewRequest = {
  leads?: string[];
  stages?: string[];
  tags?: string[];
};

// One recipient with a closed 24h window plus >= 1 template already sent
// while closed. full_name/lead_id may be absent when the backend counter row
// survives a soft-deleted contact.
export type tCampaignAtRiskRecipient = {
  handle: string;
  full_name?: string;
  lead_id?: string;
  templates_while_closed: number;
  conversation_expires_at?: string;
  last_template_sent_at?: string;
};

export type tCampaignAtRiskPreviewResult = {
  requested: number;
  at_risk_count: number;
  at_risk_recipients: tCampaignAtRiskRecipient[];
};

export type tGetCampaignAtRiskPreviewResponse = {
  message: string;
  code: number;
  result: tCampaignAtRiskPreviewResult;
};

// INT-1221: same audience-selection shape as tCampaignAtRiskPreviewRequest
// plus the template being drafted, so the modal can ask "which of these
// recipients are missing an Auto variable this template needs".
export type tCampaignMissingVariablesPreviewRequest = {
  template_id: string;
  leads?: string[];
  stages?: string[];
  tags?: string[];
};

// One recipient missing a value for >= 1 predefined ("Auto") template
// variable. full_name/lead_id are absent when no lead record was found for
// the handle at all — in that case every Auto variable the template uses is
// reported as missing.
export type tCampaignMissingVariablesRecipient = {
  handle: string;
  full_name?: string;
  lead_id?: string;
  missing_variables: string[];
  variable_values?: Record<string, string>;
};

export type tCampaignMissingVariablesPreviewResult = {
  requested: number;
  pending_count: number;
  pending_recipients: tCampaignMissingVariablesRecipient[];
};

export type tGetCampaignMissingVariablesPreviewResponse = {
  message: string;
  code: number;
  result: tCampaignMissingVariablesPreviewResult;
};

export type tCreateDraftCampaignResponse = {
  message: string;
  code: number;
  result: {
    id: number;
  } | null;
};

export type tCampaignParameter = {
  type: string;
  value: string;
  name: string;
  index?: number;
  format?: string;
};

export type tCampaignMedia = {
  type: string;
  url: string;
};

export type tCampaignRecordContact = {
  /** Backend contact record PK — kept for future API round-trips (e.g. PATCH); not used as UI identity key. Use leadId for deduplication. */
  id: number;
  leadId: string;
  name: string;
  phone: string;
};

// BUG-739: mirrors the backend's Button entity as returned by
// GetCampaignRecord (whatsapp-business-service), so an edit/draft-reload
// round-trip can restore a previously entered COPY_CODE coupon value.
export type tCampaignRecordButton = {
  type: string;
  sub_type: string;
  text?: string;
  payload: string;
  index: number;
};

export type tCampaignRecordTemplateDraft = {
  uuid: string;
  name: string;
  payload: tTemplateDraftPayload | null;
};

export type tCampaignRecord = {
  id: number;
  name: string;
  description?: string;
  status: string;
  scheduledDate?: string;
  scheduledHour?: string;
  template?: {
    id: number;
    name: string;
    lang: string;
    components: tComponent[];
  };
  // INT-1496: present only when a campaign draft points at a template draft.
  // snake_case on the wire, unlike its camelCase neighbours (bff-web
  // campaignRecordUpstreamObject.TemplateDraft).
  template_draft?: tCampaignRecordTemplateDraft;
  contacts?: tCampaignRecordContact[];
  stages?: string[];
  tags?: string[];
  parameters?: tCampaignParameter[];
  media?: tCampaignMedia;
  buttons?: tCampaignRecordButton[];
};

export type tGetCampaignRecordResponse = {
  message: string;
  code: number;
  result: {
    campaign: tCampaignRecord;
  };
};

export type tWhatsappApiProviderProps = {
  children: React.ReactNode;
};

/**
 * INT-1300 — the flow the user came to create a template *for*, set when they
 * leave the Flows list through "Usar en una plantilla". It lives on this
 * provider because it has to survive the window switch from FLOWS to
 * CREATE_TEMPLATE, which unmounts both views.
 *
 * Purely contextual: the wizard reads it to show the "Vas a usar el flow ..."
 * banner and the "Volver a Flows" link, and nothing else depends on it — a
 * template created while it is set is no different from any other.
 */
export type tPendingFlowForTemplate = {
  uuid: string;
  name: string;
};

export type tWhatsappApiHook = tTemplateDraftsState & {
  templates: tTemplateTable[];
  setTemplates: React.Dispatch<React.SetStateAction<tTemplateTable[]>>;
  templatesHydrated: boolean;
  // INT-1544 — the latest getAndParseTemplates call failed and the list still
  // holds the previous answer.
  templatesFetchFailed: boolean;
  // INT-1544 — the template a notification asked to open; TemplatesTab consumes
  // it. An object so a second click on the same notification is a new request.
  templateLink: { templateName: string } | null;
  openTemplateLink: (templateName: string) => void;
  clearTemplateLink: () => void;
  filteredTemplates: tTemplateTable[];
  setFilteredTemplates: React.Dispatch<React.SetStateAction<tTemplateTable[]>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  searchTemplates: (query: string) => Promise<void>;
  whatsappApiWindow: eWhatsappApiWindow;
  setWhatsappApiWindow: React.Dispatch<
    React.SetStateAction<eWhatsappApiWindow>
  >;
  createTemplateFormik: FormikProps<tCreateTemplateForm> | undefined;
  mediaPreview: string | null;
  setMediaPreview: React.Dispatch<React.SetStateAction<string | null>>;
  mediaType: eMediaType | null;
  setMediaType: React.Dispatch<React.SetStateAction<eMediaType | null>>;
  showInput: eSelectedInput | null;
  setShowInput: React.Dispatch<React.SetStateAction<eSelectedInput | null>>;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  editMode: boolean;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  templateToEdit: tTemplateTable | null;
  setTemplateToEdit: React.Dispatch<React.SetStateAction<tTemplateTable | null>>;
  pendingFlowForTemplate: tPendingFlowForTemplate | null;
  setPendingFlowForTemplate: React.Dispatch<
    React.SetStateAction<tPendingFlowForTemplate | null>
  >;
  /** INT-1500 U2 — the category a template created from the chat starts on. */
  pendingTemplateCategory: eTemplateCategory | null;
  setPendingTemplateCategory: React.Dispatch<
    React.SetStateAction<eTemplateCategory | null>
  >;
  injectDataToFormik: (data: tTemplateTable) => void;
  sortBy: (property: keyof tTemplateTable) => void;
  syncTemplates: () => Promise<void>;
  getAndParseTemplates: () => Promise<void>;
  usageRecord: tUsageRecord | null;
  setUsageRecord: React.Dispatch<React.SetStateAction<tUsageRecord | null>>;
  statisticsRecord: tGetStatisticsResponse | null;
  setStatisticsRecord: React.Dispatch<
    React.SetStateAction<tGetStatisticsResponse | null>
  >;
  getStatistics: (startDate?: string, endDate?: string) => Promise<void>;
  selectedPeriod: string;
  setSelectedPeriod: React.Dispatch<React.SetStateAction<string>>;
  handlePeriodChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  accountData: tGetAccountResponse | null;
  setAccountData: React.Dispatch<
    React.SetStateAction<tGetAccountResponse | null>
  >;
  // RF01 - Template edit validation functions
  validateTemplateEditEligibility: (template: tTemplateTable) => {
    canEdit: boolean;
    reason?: string;
  };
  setTemplateStatus: React.Dispatch<
    React.SetStateAction<eTemplatesTable | undefined>
  >;
  templateStatus: eTemplatesTable | undefined;
  templateCounter: tSentTemplateCounter | undefined;
  undeliveredMessage: tUndeliveredMessage | undefined;
  setTemplateCounter: React.Dispatch<React.SetStateAction<tSentTemplateCounter | undefined>>;
  accountHealthStatus: tAccountHealthStatus;
  violationType: string | null;
  purchaseOneTimeCredits: (priceId: string, paymentMethodId: string) => Promise<void>;
  refreshCredits: () => Promise<void>;
  fetchOneTimeCreditData: () => Promise<void>;
  oneTimeCreditProducts: tOneTimeCreditProduct[];
  oneTimeCreditPaymentMethods: tPaymentMethodOption[];
  fetchingOneTimeCreditData: boolean;
  // General Tab - redesign fields
  accountInfo: tAccountInfo | null;
  creditsInfo: tCreditsInfo | null;
  messageStatistics: tMessageStatistics | null;
  statisticsLoading: boolean;
  notifications: tWapiNotification[];
  notificationsLoading: boolean;
  notificationsHasMore: boolean;
  unreadCount: number;
  loadMoreNotifications: () => Promise<void>;
  markNotificationRead: (id: number) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  notificationFilter: 'pending' | 'all';
  setNotificationFilter: React.Dispatch<React.SetStateAction<'pending' | 'all'>>;
  notificationCategory: tNotificationCategory | null;
  setNotificationCategory: React.Dispatch<React.SetStateAction<tNotificationCategory | null>>;
  statisticsDateRange: tStatisticsDateRange;
  handleStatisticsDateChange: (field: 'start' | 'end', value: string) => void;
  showInvalidDateModal: boolean;
  setShowInvalidDateModal: React.Dispatch<React.SetStateAction<boolean>>;
  invalidDateReason: 'future' | 'range_exceeded' | 'start_after_end';
  showQualityModal: boolean;
  setShowQualityModal: React.Dispatch<React.SetStateAction<boolean>>;
  // GRO-811: One-Time Credits — Figma alignment (additive only)
  creditPackages: tCreditPackage[] | null;
  setCreditPackages: React.Dispatch<
    React.SetStateAction<tCreditPackage[] | null>
  >;
  /**
   * Per-credit cost in cents for the workspace currency. Drives the live
   * total in the custom-amount input. Null until the packages endpoint has
   * resolved (or when Stripe lacks a Credit Unit Price for this currency).
   */
  creditUnitPriceCents: number | null;
  setCreditUnitPriceCents: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  /**
   * Authoritative workspace currency (e.g. `"USD"`, `"MXN"`) for the credits
   * purchase flow, sourced from the Stripe-driven `/credits/packages` BFF
   * response alongside `creditUnitPriceCents`. This is the single source of
   * truth for the currency label on BOTH the preset and custom-amount paths —
   * `selectedPackage?.currency` only exists when a preset is picked, so the
   * custom path must read this instead of defaulting to a hardcoded `'MXN'`
   * (GRO-811 currency-label bug). Null until the packages endpoint resolves
   * or when the workspace has no active subscription.
   */
  creditCurrency: string | null;
  setCreditCurrency: React.Dispatch<React.SetStateAction<string | null>>;
  paymentMethods: tPaymentMethod[] | null;
  setPaymentMethods: React.Dispatch<
    React.SetStateAction<tPaymentMethod[] | null>
  >;
  creditsBalance: tCreditsBalance | null;
  setCreditsBalance: React.Dispatch<
    React.SetStateAction<tCreditsBalance | null>
  >;
  purchaseHistory: tPaginated<tPurchaseHistoryItem> | null;
  setPurchaseHistory: React.Dispatch<
    React.SetStateAction<tPaginated<tPurchaseHistoryItem> | null>
  >;
  purchaseDraft: tPurchaseDraft;
  setPurchaseDraft: React.Dispatch<React.SetStateAction<tPurchaseDraft>>;
  currentStep: tPurchaseStep;
  setCurrentStep: React.Dispatch<React.SetStateAction<tPurchaseStep>>;
};

export type tTemplateExample = {
  param_name: string;
  example: string;
};

export type tButton = {
  index: number;
  type: string;
  text: string;
  url?: string;
  phone_number?: string;
  otp?: string;
  mpm?: string;
  catalog?: string;
  flow?: string;
  voice_call?: string;
  app?: string;
  example?: string;
};

export type tComponent = {
  type: eComponentType;
  format?: eFormatType;
  text?: string;
  example?: {
    header_text_named_params?: tTemplateExample[];
    url_example?: string[];
    body_text_named_params?: tTemplateExample[];
    header_handle?: string[];
    filename?: string;
    body_text?: string[][];
  };
  buttons?: tButton[];
  // INT-1268 — only present on CAROUSEL components: each card nests its own
  // components array (HEADER media, optional BODY, optional BUTTONS).
  cards?: tCard[];
};

export type tCard = {
  components: tComponent[];
};

export type tCreateTemplateRequest = {
  name: string;
  language: string;
  category: eTemplateCategory;
  parameter_format: eParameterFormat;
  components: tComponent[];
  // Flow completo (INT-948). Root-level, mirroring the `templates` table
  // columns from INT-844/INT-848 — the backend injects the actual FLOW
  // button into components[].buttons[] when building the Meta payload.
  flow_uuid?: string;
  flow_action?: string;
  flow_cta_text?: string;
  flow_first_screen?: string;
};

export type tCreateTemplateResponse = {
  template: tTemplateTable;
  message: string;
  status: string;
};

export type tErrorMeta = {
  response: {
    data: {
      error: string;
    };
  };
};

export type tUsageRecord = {
  id: number;
  period_start: string;
  period_end: string;
  used_count: number;
  limit: number;
  is_active: boolean;
};
export type tGetCreditsResponse = {
  success: boolean;
  data: {
    feature: {
      id: number;
      code: string;
      name: string;
      description: string;
      product_type: string;
    };
    usage_summary: {
      total_used: number | null;
      total_limit: number | null;
      plan_included_credits?: number;
      additional_credits_available?: boolean;
      additional_credits_url?: string;
    };
    usage_records: tUsageRecord[];
  };
};

export type tGetStatisticsResponse = {
  data: {
    waba_id: string;
    date_range: {
      start_date: string;
      end_date: string;
    };
    statistics: {
      messages_delivered: number;
      messages_received: number;
      messages_read: number;
      total_conversations: number;
    };
    last_updated: string;
  };
  message: string;
  success: boolean;
};

export type tAccountHealthStatus =
  | 'OK'
  | 'LOW_SPAMMING'
  | 'HIGH_SPAMMING'
  | 'RESTRICTED';

export type tGetAccountResponse = {
  id: number;
  waba_id: string;
  workspace_id: string;
  name: string;
  phone_number: string;
  phone_number_id: string;
  allocation_config_id: string;
  status?: string;
  is_coex?: boolean;
  account_health_status?: tAccountHealthStatus;
  // INT-1101: Meta policy-violation code driving the specific banner
  // (SPAM, SCAM, TOBACCO, SCRIPTED_BM, USER_INITIATED_CALLS_LOW_PICKUP_RATE,
  // ...). Null/absent when account_health_status is not a meta_violation.
  violation_type?: string | null;
  // INT-1556: the Meta business (Business Manager portfolio) that owns the
  // WABA. Absent until the backend resolves it; the template rejection links
  // fall back to the generic Business Support Home.
  owner_business_id?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  quality_rating?: string;
  messaging_limit_tier?: string;
  /**
   * Resolved daily messaging limit served by whatsapp-business-service (INT-901).
   * `-1` means unlimited, `0` means unknown / unmapped tier, `>0` is the per-day allowance.
   * Prefer this over `messaging_limit_tier`; it lets the backend ship new tiers from
   * Meta without requiring a frontend deploy.
   */
  daily_messaging_limit?: number;
  /**
   * Count of unique contacts that received a business-initiated template in the
   * last 24 hours for this workspace (BUG-576). `0` when no templates have been
   * sent or on error.
   */
  daily_messages_sent?: number;
  phone_number_status?: string;
  /**
   * WABA-level business verification status sourced from Meta (INT-910).
   * Backend collapses any unrecognized value (or a Meta failure) to "UNKNOWN".
   */
  business_verification_status?: string;
  /**
   * Phone-number send eligibility sourced from Meta's health_status field.
   * Optional: a frontend bundle ahead of the backend deploy will not see it,
   * in which case the connection badge falls back to its binary state.
   */
  can_send_message?: string;
  /**
   * Meta's raw (English) explanation of a send restriction. The modal
   * categorizes it into localized copy via `resolveSendRestrictionCategory`,
   * falling back to a Spanish wrapper that quotes the raw string when no
   * category matches (INT-951). A Go backend serializes an absent value as
   * an empty string, so consumers must coalesce with `||`.
   */
  send_restriction_reason?: string;
};

export type tResult = {
  campaigns: tCampaign[];
  total_pages: number;
  current_page: number;
  /** Exact result count for the active workspace and search filter. */
  total: number;
};
export type tCampaign = {
  id: number;
  workspace_id: string;
  name: string;
  execution_date: string;
  campaign_status: string;
  funnel_stage: string;
  total_sent: number;
  total_read: number;
  read_percentage: number;
  total_delivered: number;
  delivered_percentage: number;
  total_pending: number;
  pending_percentage: number;
  total_undelivered: number;
  total_replied: number;
  /** Backend-emitted; no longer rendered in the campaigns history UI (INT-897). */
  conversations: number;
  user_id: string;
  resend_count: number;
  parent_campaign_id: number | null;
};

export type tGetCampaignsResponse = {
  code: number;
  success: boolean;
  message: string;
  result: tResult;
};

// --- General Tab: redesign types ---

export type tQualityRating = {
  rating: eQualityRating;
  label: string;
  description: string;
};

/**
 * UI-shaped business verification info the AccountInfoCard renders. `bucket`
 * collapses the 11-value Meta enum into the three visual states the design
 * supports (verified / pending / rejected) plus unknown for the degraded path.
 */
export type tBusinessVerification = {
  rawStatus: eBusinessVerificationStatus;
  bucket: eBusinessVerificationBucket;
  label: string;
};

export type tAccountInfo = {
  name: string;
  phone: string;
  qualityRating: tQualityRating;
  status: string;
  rawStatus: string;
  dailyMessageLimit: number;
  dailyMessagesSent: number;
  /**
   * Set when the backend returned a value (recognized or not). `null` when the
   * field is absent from the payload (e.g. backend deploy lag) — the card hides
   * the row in that case so we never render an empty pill.
   */
  businessVerification: tBusinessVerification | null;
  /**
   * Send eligibility from Meta's health_status. `null` when the backend has not
   * shipped the field or returned an unrecognized value — the connection badge
   * falls back to its binary connected/disconnected state in that case.
   */
  canSendMessage: eCanSendMessage | null;
  /**
   * Meta's raw (English) restriction explanation. The modal categorizes this
   * into localized copy (`resolveSendRestrictionCategory`) and falls back
   * to a Spanish wrapper that interpolates the raw string. Empty string
   * when there is no restriction.
   */
  sendRestrictionReason: string;
  /**
   * True when the backend marked this WABA as Coex (the user's display name
   * lives in the mobile WhatsApp Business app, not WhatsApp Manager).
   * Defaults to `false` on backend-deploy lag — preserves today's non-Coex
   * behavior. Used to short-circuit the connection badge to "Conectado" on
   * verified Coex workspaces even when Meta cosmetically returns
   * `can_send_message: LIMITED` (INT-951).
   */
  isCoex: boolean;
};

export type tPlanCredits = {
  used: number;
  total: number;
  periodEnd: string;
};

export type tAdditionalCredits = {
  used: number;
  total: number;
  expirationDate: string;
};

export type tCreditsInfo = {
  plan: tPlanCredits;
  additional: tAdditionalCredits;
};

export type tMessageStatistics = {
  allMessages: { sent: number; delivered: number };
  byCategory: { marketing: number; utility: number; authentication: number };
  freeMessages: { total: number; window24h: number; window72h: number };
  paidMessages: {
    total: number;
    marketing: number;
    utility: number;
    authentication: number;
  };
};

export type tAllMessages = {
  sent: number;
  delivered: number;
  after_template: number;
};

export type tDeliveredByCategory = {
  marketing: number;
  utility: number;
  authentication: number;
};

export type tFreeMessages = {
  total: number;
  window_24h: number;
  window_72h: number;
};

export type tPaidDelivered = {
  total: number;
  by_category: tDeliveredByCategory;
};

export type tDetailedStatisticsData = {
  waba_id: string;
  date_range: {
    start_date: string;
    end_date: string;
  };
  all_messages: tAllMessages;
  delivered_by_category: tDeliveredByCategory;
  free_messages: tFreeMessages;
  paid_delivered: tPaidDelivered;
  last_updated: string | null;
};

export type tGetDetailedStatisticsResponse = {
  data: tDetailedStatisticsData;
  message: string;
  success: boolean;
};

export type tNotificationSeverity = 'INFO' | 'WARNING' | 'ERROR';
export type tNotificationCategory = 'AUTOMATION' | 'GENERAL';

export type tWapiNotification = {
  id: number;
  waba_id?: string | null;
  workspace_id: string;
  notification_type: string;
  title: string;
  description: string;
  severity: tNotificationSeverity;
  is_read: boolean;
  action_url?: string | null;
  category: tNotificationCategory;
  created_at: string;
  updated_at?: string;
};

export type tGetNotificationsResponse = {
  success: boolean;
  message: string;
  data: {
    notifications: tWapiNotification[];
    next_cursor: string | null;
    has_more: boolean;
  };
};

export type tGetUnreadCountResponse = {
  success: boolean;
  message: string;
  data: {
    unread_count: number;
  };
};

export type tStatisticsDateRange = {
  start: string;
  end: string;
};

// --- GRO-811: One-Time Credits — Figma alignment ---

/** Package as returned by BFF `GET /credits/packages`. */
export type tCreditPackage = {
  id: string;
  credits: number;
  price_cents: number;
  currency: string;
  is_popular: boolean;
  sort_order: number;
  /**
   * Stripe `provider_price_id` (e.g. `price_…`). Carried through to the
   * purchase POST payload as `price_id` — the legacy
   * `OneTimeCreditController::purchase` looks up the Stripe price by this
   * id, not by the package's database id.
   */
  provider_price_id: string;
};

/** Plan-credit sub-balance (used / total + period). */
export type tPlanCreditsBalance = {
  used: number;
  total: number;
  available: number;
  period_end: string;
};

/** Additional (one-time) credit sub-balance. */
export type tAdditionalCreditsBalance = {
  used: number;
  total: number;
  available: number;
  expires_at: string;
};

/**
 * Recurring (self-service WAPI template-credit) sub-balance — RET-1487 Sub-3.
 *
 * Additive, webhook-provisioned pool emitted by leadbilling (RET-1498) on
 * `GET /credits/balance`. Snake_case mirrors the wire. `active` is the
 * entitlement signal (an enabled `workspace_feature_overrides` row); the
 * pool buckets are `product_type=NULL` so the backend derives `active` from
 * the override, not from a distinct bucket. `renews_at` is null when there
 * is no active recurring override. Optional on `tCreditsBalance` so a
 * pre-RET-1498 env (field absent) renders the empty state without crashing.
 */
export type tRecurringCreditsBalance = {
  active: boolean;
  total: number;
  renews_at: string | null;
};

/** Aggregate balance: plan + additional (+ optional recurring). Used by `CreditsTab` and `CreditsCard`. */
export type tCreditsBalance = {
  plan: tPlanCreditsBalance;
  additional: tAdditionalCreditsBalance;
  recurring?: tRecurringCreditsBalance;
};

/**
 * Stored payment method as returned by Billing.
 * Local to WhatsappApi: SubscriptionsManager has its own `tPaymentMethod`
 * with extra fields (year/month/cardholder). The credits flow only needs id+last4+brand.
 */
export type tPaymentMethod = {
  id: string;
  last4: string;
  brand: string;
};

/** Single purchase row rendered in `PurchaseHistoryModal`. */
export type tPurchaseHistoryItem = {
  id: string;
  credits: number;
  price_cents: number;
  expires_at: string;
  // Wire contract emitted by leadbilling's `FeatureUsageRepository::
  // getOneTimeCreditPurchases` is lowercase. The earlier UPPERCASE typing
  // made `mapStatusVariant`'s switch fall through to the default branch
  // for every row, so every purchase rendered as "Consumido" regardless
  // of state. Backend tests pin lowercase, so the front aligns here.
  status: 'active' | 'expired' | 'consumed';
  created_at: string;
};

/** Generic paginated wrapper. */
export type tPaginated<T> = {
  items: T[];
  page: number;
  total_pages: number;
  total: number;
};

/**
 * Modal step for the 3-step purchase flow.
 *
 * The `recurring_*` steps (RET-1487 Sub-3) are a parallel track for the
 * self-service recurring-credits modal. They share `currentStep` with the
 * one-time flow so the two are mutually exclusive — the one-time render
 * blocks key off the bare `'select'|'confirm'|'success'` values and ignore
 * the `recurring_*` members.
 */
export type tPurchaseStep =
  | 'idle'
  | 'select'
  | 'confirm'
  | 'success'
  | 'recurring_select'
  | 'recurring_confirm'
  | 'recurring_success';

/** Draft payload accumulated across Step 1 → Step 2 → submit. */
export type tPurchaseDraft = {
  packageId?: string;
  customCredits?: number;
  paymentMethodId?: string;
};

/** Aggregated state of the purchase flow exposed by `WhatsappApi.context`. */
export type tPurchaseFlowState = {
  currentStep: tPurchaseStep;
  draft: tPurchaseDraft;
};

export type tGetCampaignsStatisticsResponse = {
  code: number;
  success: boolean;
  message: string;
  result: {
    statistics: {
      workspace_id: string;
      total_campaigns: number;
      reached_leads: number;
      conversion_rate: number;
    };
  };
};

// --- Campaigns Redesign types ---

export type tCampaignMetrics = {
  sent: number;
  delivered: number;
  read: number;
  replied: number;
  // INT-833: distinct accounts that replied to a campaign template within the
  // configured reply window (default 72h), excluding EU/UK/South Korea/Japan.
  uniqueConversations: number;
};

export type tCampaignMetricsResponse = {
  message: string;
  code: number;
  result: {
    metrics: tCampaignMetrics;
  };
};

export type tCampaignDetail = {
  id: number;
  name: string;
  templateName: string;
  executionDate: string;
  status: string;
  targetUsers: number;
  sent: number;
  delivered: number;
  /** Backend-emitted; no longer rendered in the campaign detail modal (INT-897). */
  conversations: number;
  undelivered: number;
  /**
   * Recipients the send deliberately held back because Meta's per-recipient
   * marketing frequency limit was still in force for them (INT-1314). Counted
   * in neither `sent` nor `undelivered`: nothing was attempted and nothing was
   * rejected. Optional so a response from a backend that predates the field
   * still parses.
   */
  skipped?: number;
  resendDate?: string | null;
  resendTime?: string | null;
  resendCount?: number;
  parentCampaignId?: number | null;
  /** BFF-resolved creator display name. Empty string when unresolved (legacy NULL user_id or lookup failure). */
  userName?: string;
  heldForQualityCount?: number;
  qualityRejectedCount?: number;
  accountDisconnected?: boolean;
  feedbackTitle?: string | null;
  feedbackMessage?: string | null;
  resendPending?: boolean;
  failureBreakdown?: { code: number; count: number; group: eFailureGroup }[];
  totalFailed?: number;
  /** ISO-8601 cancellation timestamp (INT-936). Present only when status === 'cancelled'. */
  cancelledAt?: string | null;
  /** BFF-resolved cancelling user display name (INT-936). Empty string when unresolved. */
  cancelledByName?: string;
};

export type tUndeliveredLead = {
  id: number;
  leadId: string;
  name: string;
  phone: string;
  failureCode?: number | null;
};

export type tGetUndeliveredLeadsResponse = {
  code: number;
  success: boolean;
  message: string;
  result: {
    leads: tUndeliveredLead[];
    total: number;
    current_page: number;
    total_pages: number;
  };
};

export type tFunnelStageSummary = {
  uuid: string;
  name: string;
  leads_count: number;
};

export type tFunnelSummary = {
  uuid: string;
  name: string;
  stages: tFunnelStageSummary[];
};

export type tGetFunnelsSummaryResponse = {
  success: boolean;
  data: {
    funnels: tFunnelSummary[];
  };
};
